import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, track, availLabel, checkoutOriginUrl, goToCheckout } from "../api";
import { isNative } from "../mobileAuth";
import { useAuth } from "../AuthContext";
import { Page, SectionTitle, WhatsAppButton } from "../components/Layout";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { Wallet, Calendar as CalendarIcon } from "lucide-react";

const PENDING_KEY = "mas_pending_booking";

const PKG = {
  open_space: [
    { id: "os_1", label: "Giornata Singola", price: "45 €", per: "" },
    { id: "os_5", label: "5 Giornate", price: "210 €", per: "42 €/giorno" },
    { id: "os_10", label: "10 Giornate", price: "390 €", per: "39 €/giorno" },
  ],
  cabina: [
    { id: "cab_1", label: "Giornata Singola", price: "55 €", per: "" },
    { id: "cab_5", label: "5 Giornate", price: "260 €", per: "52 €/giorno" },
    { id: "cab_10", label: "10 Giornate", price: "490 €", per: "49 €/giorno" },
  ],
};

const TYPE_LABEL = { open_space: "Open Space", cabina: "Cabina Privacy" };

function StateBadge({ state }) {
  const cls = { "DISPONIBILE": "state-DISPONIBILE", "ULTIMA POSTAZIONE": "state-ULTIMA", "COMPLETO": "state-COMPLETO" }[state] || "state-DISPONIBILE";
  return <span className={`badge-chip ${cls}`}>{state}</span>;
}

export default function PrenotaPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const pkgRef = useRef(null);
  const [type, setType] = useState("open_space");
  const [date, setDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [av, setAv] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [bal, setBal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [buyPkg, setBuyPkg] = useState(null);
  const [pkgCheckoutUrl, setPkgCheckoutUrl] = useState(null);
  const [pkgPreparing, setPkgPreparing] = useState(false);
  const [flexBusy, setFlexBusy] = useState(false);
  const [resSubs, setResSubs] = useState([]);
  const [dateOpen, setDateOpen] = useState(false);

  const approved = user && user.role === "artist" && user.approved && user.status !== "suspended";

  // Ripristina data/tipo dopo il rientro dal pagamento Stripe (se presenti).
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      if (raw) {
        const p = JSON.parse(raw);
        if (p?.date) setDate(p.date);
        if (p?.type) setType(p.type);
        localStorage.removeItem(PENDING_KEY);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { api.get("/config").then((r) => setCfg(r.data)); }, []);

  const loadBalance = () => {
    if (!approved) { setBal(null); return; }
    api.get("/my/balance").then((r) => setBal(r.data)).catch(() => setBal(null));
  };
  useEffect(loadBalance, [approved]);

  useEffect(() => {
    if (!approved) { setResSubs([]); return; }
    api.get("/my/resident").then((r) => setResSubs(r.data)).catch(() => setResSubs([]));
  }, [approved]);
  const reservedForDate = resSubs.find((s) => s.type === "RESIDENT_RESERVED" && s.status === "active"
    && (s.start_date || "") <= date && date <= (s.last_usable_date || ""));

  useEffect(() => {
    if (!date) return;
    track("availability_viewed");
    let stale = false;
    api.get(`/availability?date=${date}&station_type=${type}`)
      .then((r) => { if (!stale) setAv(r.data); })
      .catch(() => { if (!stale) setAv(null); });
    return () => { stale = true; };
  }, [date, type]);

  const flexRem = bal ? (bal.flex_remaining || 0) : 0;
  const credit = (bal ? (bal[type] || 0) : 0) + flexRem;

  const guard = () => {
    if (!user) { toast.info("Accedi come artista per prenotare"); nav("/accedi"); return false; }
    if (user.role === "customer") { toast.error("Il coworking è riservato agli artisti."); return false; }
    if (user.role === "artist" && !approved) { toast.error("Profilo in approvazione: appena approvato potrai prenotare."); return false; }
    return true;
  };

  const scrollToPackages = () => {
    track("zero_balance_buy_cta", { type });
    pkgRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

    const book = async () => {
    if (!guard()) return;
    if (credit <= 0 && !reservedForDate) { scrollToPackages(); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { date, station_type: type });
      toast.success(data && data.credit_source === "reserved"
        ? (type === "cabina"
            ? "Cabina Privacy confermata — inclusa nel tuo Resident Reserved."
            : "Open Space confermata — postazione garantita Resident Reserved.")
        : data && data.credit_source === "flex"
        ? "Prenotazione confermata. Hai utilizzato 1 giornata Resident Flex."
        : "Prenotazione confermata. Hai utilizzato 1 giornata del tuo pacchetto.");
      const r = await api.get(`/availability?date=${date}&station_type=${type}`); setAv(r.data);
      loadBalance();
    } catch (e) {
      const st = e?.response?.status;
      if (st === 402) { toast.info("Non hai più giornate disponibili."); loadBalance(); scrollToPackages(); }
      else if (st === 409) { toast.info("Data completa"); const r = await api.get(`/availability?date=${date}&station_type=${type}`); setAv(r.data); }
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  const requestOffline = async (pid) => {
    try {
      const { data } = await api.post("/packages/request", { package_id: pid });
      window.open(`https://wa.me/393715233268?text=${encodeURIComponent(data.message)}`, "_blank");
    } catch (e) { toast.error(apiErr(e)); }
  };

  // Apre il dialog di acquisto pacchetto. Per il pagamento online pre-creiamo la sessione
  // Stripe (prefetch) così il tap finale è un <a target="_top"> nativo, compatibile con
  // Safari iOS dentro l'iframe Preview. Nessun accredito/giornata prima del pagamento.
  const buyFlex = async () => {
    if (!guard()) return;
    setFlexBusy(true);
    try {
      const { data } = await api.post("/resident/checkout", { plan_id: "resident_flex", origin_url: checkoutOriginUrl(), native: isNative() });
      if (data && data.checkout_url) { goToCheckout(data.checkout_url); }
      else { toast.error("Pagamento non disponibile"); setFlexBusy(false); }
    } catch (e) { toast.error(apiErr(e)); setFlexBusy(false); }
  };

  const openBuy = (p) => {
    if (!guard()) return;
    track("package_purchased", { started: true });
    if (cfg && !cfg.online_payment) { requestOffline(p.id); return; }
    try { localStorage.setItem(PENDING_KEY, JSON.stringify({ date, type })); } catch { /* ignore */ }
    setBuyPkg(p);
  };

  useEffect(() => {
    if (!buyPkg) { setPkgCheckoutUrl(null); return; }
    let stale = false;
    setPkgPreparing(true); setPkgCheckoutUrl(null);
    api.post("/packages/checkout", { package_id: buyPkg.id, origin_url: checkoutOriginUrl(), native: isNative() })
      .then((r) => { if (!stale) setPkgCheckoutUrl(r.data && r.data.checkout_url ? r.data.checkout_url : null); })
      .catch((e) => { if (!stale) { setPkgCheckoutUrl(null); setBuyPkg(null); toast.error(apiErr(e)); } })
      .finally(() => { if (!stale) setPkgPreparing(false); });
    return () => { stale = true; };
  }, [buyPkg]); // eslint-disable-line

  const showZeroBalance = approved && av && av.state !== "COMPLETO" && credit <= 0 && !reservedForDate;
  const showBook = approved && av && av.state !== "COMPLETO" && (credit > 0 || !!reservedForDate);

  return (
    <Page>
      <SectionTitle sub="Il tuo studio, quando ti serve. Seleziona data e tipo di postazione.">Coworking</SectionTitle>

      {user && user.role === "customer" && (
        <div className="card p-4 mb-4 bg-violet-50 border-violet-200 text-sm text-violet-800" data-testid="customer-notice">Il coworking è riservato agli artisti. Come cliente puoi scoprire gli artisti presenti e i servizi MAS.</div>
      )}
      {user && user.role === "artist" && !approved && (
        <div className="card p-4 mb-4 bg-amber-50 border-amber-200" data-testid="pending-notice">
          <div className="font-semibold text-amber-800">Profilo in approvazione</div>
          <div className="text-sm text-amber-700 mt-1">MAS sta verificando il tuo profilo. Appena approvato potrai prenotare. Intanto puoi vedere prezzi e disponibilità.</div>
        </div>
      )}

      <div className="flex gap-2 mb-4">
        <button data-testid="type-openspace" onClick={() => setType("open_space")}
          className={`flex-1 py-3 rounded-xl text-sm font-medium border ${type === "open_space" ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200"}`}>Open Space</button>
        <button data-testid="type-cabina" onClick={() => setType("cabina")}
          className={`flex-1 py-3 rounded-xl text-sm font-medium border ${type === "cabina" ? "border-violet-600 text-violet-700 bg-violet-50" : "border-gray-200"}`}>Cabina Privacy</button>
      </div>

      <div className="card p-5">
        {approved && (
          <div className="text-sm text-gray-600 mb-3" data-testid="balance-indicator">
            <div className="flex items-center gap-2">
              <Wallet size={16} className="text-violet-600" />
              <span>Giornate {TYPE_LABEL[type]} (pacchetto): <b data-testid="balance-count" className="text-gray-900">{bal ? (bal[type] || 0) : 0}</b></span>
            </div>
            {flexRem > 0 && (
              <div className="flex items-center gap-2 mt-1 text-violet-700" data-testid="flex-balance-indicator">
                <Wallet size={16} className="text-violet-600" />
                <span>Resident Flex: <b data-testid="flex-balance-count">{flexRem}/20</b> giornate residue · monte unico usabile su Open Space o Cabina (ogni prenotazione consuma 1)</span>
              </div>
            )}
          </div>
        )}
        <label className="block text-sm text-gray-600">Data
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <button type="button" data-testid="booking-date"
                className="w-full text-left border border-gray-200 rounded-xl px-4 py-3 mt-1 flex items-center justify-between focus:border-violet-500 outline-none">
                <span className="capitalize">{date ? format(parseISO(date), "EEEE d MMMM yyyy", { locale: it }) : "Seleziona una data"}</span>
                <CalendarIcon size={18} className="text-violet-600 shrink-0 ml-2" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white shadow-lg" align="start" data-testid="booking-date-popover">
              <Calendar
                mode="single"
                locale={it}
                selected={date ? parseISO(date) : undefined}
                onSelect={(d) => { if (d) { setDate(format(d, "yyyy-MM-dd")); setDateOpen(false); } }}
                disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </label>
        {av && (
          <div className="mt-4 flex items-center justify-between">
            <span className="text-sm text-gray-500">Stato</span>
            <StateBadge state={av.state} />
          </div>
        )}

        {reservedForDate && (
          <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="reserved-badge">
            {type === "open_space"
              ? "★ Postazione garantita — inclusa nel tuo Resident Reserved."
              : "Cabina Privacy inclusa nel tuo Resident Reserved, se disponibile."}
          </div>
        )}

        {showBook && (
          <button data-testid="book-btn" disabled={busy} onClick={book} className="btn-primary w-full mt-4">
            {busy ? "Attendere…" : "Prenota"}
          </button>
        )}

        {showZeroBalance && (
          <div className="mt-4 rounded-xl border border-violet-200 bg-violet-50 p-4 text-center" data-testid="zero-balance">
            <div className="font-semibold text-violet-900" data-testid="zero-balance-title">Non hai più giornate disponibili.</div>
            <div className="text-sm text-violet-700 mt-1" data-testid="zero-balance-sub">Acquista una giornata singola o un pacchetto per continuare con la prenotazione.</div>
            <button data-testid="zero-balance-buy" onClick={scrollToPackages} className="btn-primary w-full mt-4">Acquista giornate</button>
          </div>
        )}

        {av && av.state === "COMPLETO" && (
          <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-600" data-testid="complete-notice">
            Questa data è al completo per {TYPE_LABEL[type]}. {availLabel("COMPLETO", type)} Scegli un'altra data dal calendario.
          </div>
        )}
      </div>

      <div ref={pkgRef} className="mt-10 scroll-mt-20">
        <h2 className="serif text-2xl md:text-3xl tracking-tight">Acquista giornate {TYPE_LABEL[type]}</h2>
        <p className="text-sm text-gray-500 mt-1">Le giornate acquistate resteranno disponibili nel tuo profilo e potrai prenotarle quando vuoi.</p>
        <p className="text-xs text-gray-400 mt-1 mb-4">Validità: pacchetti 5 e 10 giornate 2 mesi dalla data di acquisto. Le giornate singole non hanno scadenza.</p>
        <div className="space-y-3">
          {PKG[type].map((p) => {
            const single = p.id.endsWith("_1");
            const label = cfg && !cfg.online_payment ? "Richiedi" : single ? "Acquista" : "Acquista pacchetto";
            return (
              <div key={p.id} className="card p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold">{p.label}</div>
                  {p.per && <div className="text-xs text-gray-500">{p.per}</div>}
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-lg font-bold whitespace-nowrap">{p.price}</div>
                  <button data-testid={`buy-${p.id}`} onClick={() => openBuy(p)} className="btn-primary text-sm py-2 px-4">{label}</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {user && user.role === "artist" && (
        <div className="mt-8 card p-5 border-violet-200 bg-violet-50/40" data-testid="resident-flex-card">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="serif text-xl">Resident Flex</div>
              <div className="text-sm text-gray-600 mt-1">20 giornate · 31 giorni · <b>22,45 €/giorno</b> utilizzandole tutte</div>
              <div className="text-xs text-gray-500 mt-1">Open Space o Cabina Privacy secondo disponibilità reale. 1 prenotazione = 1 credito.</div>
            </div>
            <div className="text-lg font-bold whitespace-nowrap shrink-0">449 €</div>
          </div>
          <button data-testid="buy-resident-flex" disabled={!approved || flexBusy} onClick={buyFlex}
            className={`w-full mt-3 ${approved ? "btn-primary" : "btn-outline opacity-60 cursor-not-allowed"}`}>
            {flexBusy ? "Preparazione…" : "Acquista Resident Flex"}
          </button>
        </div>
      )}

      <div className="mt-8 text-center">
        <WhatsAppButton msg="Ciao MAS STUDIO Rivoli (TO), vorrei informazioni sul coworking." label="Informazioni su WhatsApp" testid="prenota-whatsapp" />
      </div>

      <Dialog open={!!buyPkg} onOpenChange={(o) => !o && setBuyPkg(null)}>
        <DialogContent data-testid="buy-dialog">
          <DialogHeader>
            <DialogTitle>Acquista {buyPkg?.label}</DialogTitle>
            <DialogDescription>
              {TYPE_LABEL[type]} · {buyPkg?.price}. Pagamento sicuro su Stripe. Le giornate vengono accreditate solo dopo il pagamento confermato.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2 mt-2">
            <button data-testid="buy-abort" onClick={() => setBuyPkg(null)} className="btn-outline flex-1">Annulla</button>
            {pkgCheckoutUrl ? (
              <a data-testid="buy-confirm" href={pkgCheckoutUrl} target="_top" rel="noopener"
                 onClick={(e) => { if (isNative()) { e.preventDefault(); goToCheckout(pkgCheckoutUrl); } }}
                 className="btn-primary flex-1 text-center">Paga {buyPkg?.price}</a>
            ) : (
              <button data-testid="buy-confirm" disabled className="btn-outline flex-1 opacity-50 cursor-not-allowed">{pkgPreparing ? "Preparazione…" : "Non disponibile"}</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
